/**
 * Genius mode: audio under `public/assets/sounds/{basePath}/{key}.mp3` or `.wav`.
 */

import { getQuizSfxEnabled } from '../audio/quizSfx';

export const QUIZ_GENIUS_SOUNDS_ROOT = '/assets/sounds';

function soundAssetOriginPrefix(): string {
  const raw = process.env.REACT_APP_IMAGE_ASSET_ORIGIN;
  if (raw == null || !String(raw).trim()) return '';
  return String(raw).trim().replace(/\/$/, '');
}

function sanitizePathSegment(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const t = raw.trim().replace(/\\/g, '/');
  if (t.includes('..')) return null;
  const segments = t.split('/').filter(Boolean);
  for (const seg of segments) {
    if (!/^[a-zA-Z0-9_-]+$/.test(seg)) return null;
  }
  return segments.join('/');
}

function sanitizeFileKey(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const s = raw.trim().replace(/\.(mp3|wav|ogg)$/i, '');
  if (!/^[a-zA-Z0-9_-]+$/.test(s)) return null;
  return s;
}

function geniusSoundBaseUrl(basePath: string, key: string): string {
  const origin = soundAssetOriginPrefix();
  const pub = origin ? '' : process.env.PUBLIC_URL || '';
  const pathAfterOrigin = `${pub}${QUIZ_GENIUS_SOUNDS_ROOT}/${basePath}/${key}`;
  return origin ? `${origin}${pathAfterOrigin}` : pathAfterOrigin;
}

export function quizGeniusSoundUrlsFromKey(
  basePath: string | null | undefined,
  key: string | null | undefined
): { mp3: string; wav: string } | null {
  const b = sanitizePathSegment(basePath);
  const k = sanitizeFileKey(key);
  if (!b || !k) return null;
  const base = geniusSoundBaseUrl(b, k);
  return { mp3: `${base}.mp3`, wav: `${base}.wav` };
}

/** @deprecated Prefer {@link playGeniusSoundFromKey} for mp3/wav fallback. */
export function quizGeniusSoundSrcFromKey(
  basePath: string | null | undefined,
  key: string | null | undefined
): string | null {
  return quizGeniusSoundUrlsFromKey(basePath, key)?.mp3 ?? null;
}

function playAudioUrl(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const audio = new Audio(src);
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      audio.onended = null;
      audio.onerror = null;
      resolve(ok);
    };
    audio.onended = () => finish(true);
    audio.onerror = () => finish(false);
    void audio.play().catch(() => finish(false));
  });
}

/** Plays the clip for the key, trying `.mp3` first then `.wav`. */
export async function playGeniusSoundFromKey(
  basePath: string | null | undefined,
  key: string | null | undefined
): Promise<void> {
  if (!getQuizSfxEnabled()) return;
  const urls = quizGeniusSoundUrlsFromKey(basePath, key);
  if (!urls) return;
  if (await playAudioUrl(urls.mp3)) return;
  await playAudioUrl(urls.wav);
}
